import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Textarea,
} from '@heroui/react';
import { loadStripe } from '@stripe/stripe-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CheckoutAuthChoice from '../components/Checkout/CheckoutAuthChoice';
import SEOHead from '../components/SEOHead';
import StripeCheckout from '../components/StripeCheckout';
import { runtimeConfig } from '../config/runtime';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLocale } from '../contexts/LocaleContext';
import { orderService } from '../services/orderService';
import { paymentService } from '../services/paymentService';
import { CheckoutFormData } from '../types/checkout';
import { SelectedExtra } from '../types/product';
import {
  CreatePaymentIntentRequest,
  StripeAddressValue,
} from '../types/payment';

/**
 * Region locked to UK: shipping uses fixed locale cost from backend (Shipping.UseFixedLocaleCost).
 * Fallback when estimate API fails - matches backend UkFixedShippingCostGb.
 * Set backend Shipping.UseFixedLocaleCost=false to re-enable address-based dynamic calculation.
 */
const UK_LOCALE_SHIPPING_COST_GBP = 4.99;

const Checkout: React.FC = () => {
  const pageTitle = 'Checkout';
  const pageDescription = 'Complete your order and payment securely.';
  const navigate = useNavigate();
  const {
    cart,
    getCartTotal,
    clearCart,
    isLoading: cartLoading,
    refreshCart,
  } = useCart();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { formatCurrency, convertPrice, currentLocale } = useLocale();
  const [isInitialized, setIsInitialized] = useState(false);
  const [cartRefreshed, setCartRefreshed] = useState(false);

  // State for payment provider initialization
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [isPaymentConfigLoaded, setIsPaymentConfigLoaded] = useState(false);

  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [paymentIntentData, setPaymentIntentData] = useState<any>(null);
  const [orderPricing, setOrderPricing] = useState<{
    shippingCost: number;
    taxAmount: number;
    totalAmount: number;
  } | null>(null);

  const [formData, setFormData] = useState<CheckoutFormData>({
    notes: '',
  });

  // Checkout step: auth -> payment (address + payment collected together via Stripe)
  const [checkoutStep, setCheckoutStep] = useState<'auth' | 'payment'>('auth');

  // Guest checkout fields (from auth step)
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const hasTriggeredAutoInitRef = useRef(false);
  const [lastEstimateKey, setLastEstimateKey] = useState('');

  const [errors, setErrors] = useState<
    Partial<CheckoutFormData> & {
      guestEmail?: string;
      guestPhone?: string;
    }
  >({});

  // Initialize payment providers after runtime config is loaded
  useEffect(() => {
    const initializePaymentProviders = async () => {
      try {
        // Ensure runtime config is initialized
        await runtimeConfig.initialize();

        // Initialize Stripe
        const stripeKey = runtimeConfig.get('STRIPE_PUBLISHABLE_KEY');
        if (stripeKey) {
          setStripePromise(loadStripe(stripeKey));
        }

        setIsPaymentConfigLoaded(true);
      } catch (error) {
        toast.error('Failed to initialize payment providers');
      }
    };

    initializePaymentProviders();
  }, []);

  useEffect(() => {
    // Wait for cart to finish loading before checking if it's empty
    if (cartLoading) {
      return;
    }

    // If cart is empty and we haven't tried refreshing yet, try refreshing once
    if ((!cart || !cart.items || cart.items.length === 0) && !cartRefreshed) {
      setCartRefreshed(true);
      refreshCart();
      return;
    }

    // If cart is still empty after refresh, show error
    if ((!cart || !cart.items || cart.items.length === 0) && cartRefreshed) {
      toast.error(
        'Your cart is empty or has expired. Please add items to your cart.'
      );
      navigate('/cart');
      return;
    }

    // Cart is valid, mark as initialized
    if (cart && cart.items && cart.items.length > 0) {
      setIsInitialized(true);
    }
  }, [cart, cartLoading, cartRefreshed, refreshCart, navigate, isInitialized]);

  // Logged-in users skip auth step – go straight to payment
  useEffect(() => {
    if (
      !authLoading &&
      isAuthenticated &&
      user?.email &&
      checkoutStep === 'auth'
    ) {
      setCheckoutStep('payment');
    }
  }, [authLoading, isAuthenticated, user?.email, checkoutStep]);

  // Reset payment state and auto-init flag when entering payment step
  useEffect(() => {
    if (checkoutStep === 'payment') {
      setOrderId(null);
      setOrderNumber(null);
      setPaymentIntentData(null);
    } else {
      hasTriggeredAutoInitRef.current = false;
    }
  }, [checkoutStep]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<CheckoutFormData> & {
      guestEmail?: string;
      guestPhone?: string;
    } = {};

    if (!isAuthenticated && checkoutStep === 'payment') {
      if (!guestEmail?.trim()) {
        newErrors.guestEmail = 'Email is required';
      } else if (guestEmail && !/\S+@\S+\.\S+/.test(guestEmail)) {
        newErrors.guestEmail = 'Please enter a valid email address';
      }
      if (guestPhone && guestPhone.length > 20) {
        newErrors.guestPhone = 'Phone number is too long';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [checkoutStep, guestEmail, guestPhone, isAuthenticated]);

  // Ready to initiate payment when: auth done, and for guests we have email+phone
  const isPaymentReady =
    (isAuthenticated && !!user?.email) ||
    (!!guestEmail?.trim() &&
      /\S+@\S+\.\S+/.test(guestEmail) &&
      !!guestPhone?.trim());

  const formatAmount = useCallback(
    (amount: number) => formatCurrency(convertPrice(amount)),
    [convertPrice, formatCurrency]
  );

  const handleInputChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  /** Placeholder used when creating order before user fills Address Element; overwritten on payment success */
  const PLACEHOLDER_SHIPPING = {
    shippingName: 'Address pending',
    shippingAddress: 'Address pending',
    shippingCity: 'Pending',
    shippingState: 'N/A',
    shippingZipCode: 'SW1A 1AA',
    shippingCountry: 'GB',
  };

  /** Build shipping info from Stripe Address Element value for updateShippingInfo */
  const buildShippingInfoFromStripe = useCallback(
    (address: StripeAddressValue) => ({
      shippingName: address.name,
      shippingAddress:
        address.address.line1 +
        (address.address.line2 ? `, ${address.address.line2}` : ''),
      shippingCity: address.address.city,
      shippingState: address.address.state || 'N/A',
      shippingZipCode: address.address.postal_code,
      shippingCountry: address.address.country || 'GB',
      shippingPhoneNumber: address.phone || '',
    }),
    []
  );

  // Call estimate straight away with UK locale (placeholder address) - order summary shows shipping/total as soon as cart is ready
  useEffect(() => {
    const estimate = async () => {
      if (!cart?.id) return;

      // Use placeholder for initial estimate; real address comes from Stripe Address Element on payment
      const estimateKey = JSON.stringify({ cartId: cart.id });

      if (estimateKey === lastEstimateKey) return;

      setLastEstimateKey(estimateKey);
      try {
        setIsEstimating(true);
        const estimateResponse = await orderService.estimateOrder({
          cartId: cart.id,
          ...PLACEHOLDER_SHIPPING,
        });
        setOrderPricing({
          shippingCost: estimateResponse.shippingCost || 0,
          taxAmount: estimateResponse.taxAmount || 0,
          totalAmount: estimateResponse.totalAmount || getCartTotal(),
        });
      } catch (error: any) {
        toast.error(
          error.response?.data?.message || 'Failed to update estimate'
        );
        // Fallback to fixed locale cost when estimate fails (region locked to UK)
        const cartTotal = getCartTotal();
        setOrderPricing({
          shippingCost: UK_LOCALE_SHIPPING_COST_GBP,
          taxAmount: 0,
          totalAmount: cartTotal + UK_LOCALE_SHIPPING_COST_GBP,
        });
      } finally {
        setIsEstimating(false);
      }
    };

    estimate();
  }, [cart?.id, getCartTotal, lastEstimateKey]);

  const createOrder = useCallback(async () => {
    if (!cart?.id) {
      toast.error('Cart is empty');
      return null;
    }

    try {
      setIsCreatingOrder(true);
      // Placeholder address - will be overwritten with entered address on payment success
      const orderData: any = {
        cartId: cart.id,
        ...PLACEHOLDER_SHIPPING,
        shippingPhoneNumber: isAuthenticated
          ? user?.phoneNumber || 'Pending'
          : guestPhone || 'Pending',
        notes: formData.notes,
      };

      if (!isAuthenticated) {
        if (!guestEmail?.trim()) {
          toast.error('Email is required');
          return null;
        }
        orderData.guestEmail = guestEmail;
        orderData.guestPhoneNumber = guestPhone || 'Pending';
        const newOrder = await orderService.createGuestOrder(orderData);
        setOrderId(newOrder.id);
        setOrderNumber(newOrder.orderNumber || null);
        setOrderPricing({
          shippingCost: newOrder.shippingCost || 0,
          taxAmount: newOrder.taxAmount || 0,
          totalAmount: newOrder.totalAmount || getCartTotal(),
        });
        return newOrder;
      } else {
        const newOrder = await orderService.createOrder(orderData);
        setOrderId(newOrder.id);
        setOrderNumber(newOrder.orderNumber || null);
        setOrderPricing({
          shippingCost: newOrder.shippingCost || 0,
          taxAmount: newOrder.taxAmount || 0,
          totalAmount: newOrder.totalAmount || getCartTotal(),
        });
        return newOrder;
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create order');
      return null;
    } finally {
      setIsCreatingOrder(false);
    }
  }, [
    cart,
    formData,
    isAuthenticated,
    guestEmail,
    guestPhone,
    user?.phoneNumber,
    getCartTotal,
  ]);

  const initiatePayment = useCallback(async () => {
    let currentOrderId = orderId;

    if (!isPaymentReady) {
      toast.error('Please complete the auth step to continue');
      return;
    }

    if (!currentOrderId) {
      const tempOrder = await createOrder();
      if (!tempOrder) return;
      currentOrderId = tempOrder.id;
      setOrderId(currentOrderId);
    }

    try {
      setIsCreatingPayment(true);

      const currentUrl = window.location.origin;
      const request: CreatePaymentIntentRequest = {
        orderId: currentOrderId,
        paymentProvider: 'Stripe',
        returnUrl: `${currentUrl}/checkout?success=true`,
        cancelUrl: `${currentUrl}/checkout?canceled=true`,
      };

      if (!isAuthenticated && guestEmail) {
        (request as any).guestEmail = guestEmail;
      }

      // Validate that we have a valid order ID
      if (
        !currentOrderId ||
        !Number.isInteger(currentOrderId) ||
        currentOrderId <= 0
      ) {
        throw new Error(`Invalid order ID: ${currentOrderId}`);
      }

      const paymentIntent = await paymentService.createPaymentIntent(request);
      setPaymentIntentData(paymentIntent);
    } catch (error: any) {
      let errorMessage = 'Failed to initiate payment';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = Object.values(
          error.response.data.errors
        ).flat();
        errorMessage = `Validation errors: ${validationErrors.join(', ')}`;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsCreatingPayment(false);
    }
  }, [orderId, createOrder, isAuthenticated, guestEmail, isPaymentReady]);

  // Auto-initiate payment (create order + PaymentIntent) when entering payment step - surfaces Stripe form immediately.
  // Only trigger once per payment step entry to avoid retry loop when createOrder fails.
  useEffect(() => {
    if (
      checkoutStep === 'payment' &&
      isPaymentReady &&
      !paymentIntentData?.clientSecret &&
      !isCreatingOrder &&
      !isCreatingPayment &&
      isPaymentConfigLoaded &&
      stripePromise &&
      !hasTriggeredAutoInitRef.current
    ) {
      hasTriggeredAutoInitRef.current = true;
      initiatePayment();
    }
  }, [
    checkoutStep,
    isPaymentReady,
    paymentIntentData?.clientSecret,
    isCreatingOrder,
    isCreatingPayment,
    isPaymentConfigLoaded,
    stripePromise,
    initiatePayment,
  ]);

  const handlePaymentSuccess = async (enteredAddress: StripeAddressValue) => {
    try {
      // ALWAYS stamp the entered address on the order - never use placeholder/default
      if (!enteredAddress?.address) {
        toast.error(
          'Payment successful but address was not captured. Please contact support with your order number.'
        );
      } else if (orderId) {
        const shippingInfo = {
          ...buildShippingInfoFromStripe(enteredAddress),
          shippingPhoneNumber:
            enteredAddress.phone || guestPhone || 'Not provided',
        };
        try {
          if (isAuthenticated) {
            await orderService.updateShippingInfo(orderId, shippingInfo);
          } else if (orderNumber && guestEmail) {
            await orderService.updateGuestShippingInfo({
              orderNumber,
              email: guestEmail,
              ...shippingInfo,
            });
          }
        } catch (error) {
          toast.error(
            'Payment successful but failed to update shipping address. Please contact support.'
          );
        }
      }

      toast.success('Payment successful!');
      clearCart();

      if (isAuthenticated) {
        navigate('/account/orders');
      } else if (orderNumber && guestEmail) {
        const params = new URLSearchParams({
          orderNumber,
          email: guestEmail,
        });
        navigate(`/guest-order-tracking?${params.toString()}`);
      } else {
        navigate('/guest-order-tracking');
      }
    } catch (error: any) {
      toast.error(
        'Payment successful but failed to complete order. Please contact support.'
      );
    }
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
  };

  const getShippingCost = () => orderPricing?.shippingCost;
  const getTaxAmount = () => orderPricing?.taxAmount;
  const getTotal = () =>
    orderPricing?.totalAmount ??
    getCartTotal() + (orderPricing?.shippingCost || 0);

  // Show loading while cart is loading or being refreshed
  if (cartLoading || (!isInitialized && !cartRefreshed)) {
    return (
      <>
        <SEOHead title={pageTitle} description={pageDescription} />
        <div className='flex justify-center items-center min-h-screen'>
          <div className='text-center'>
            <Spinner size='lg' />
            <p className='mt-4 text-gray-600'>
              {cartLoading ? 'Loading your cart...' : 'Refreshing cart...'}
            </p>
          </div>
        </div>
      </>
    );
  }

  // Show empty cart message after cart has loaded and been refreshed
  if ((!cart || !cart.items || cart.items.length === 0) && cartRefreshed) {
    return (
      <>
        <SEOHead title={pageTitle} description={pageDescription} />
        <div className='flex justify-center items-center min-h-screen'>
          <div className='text-center'>
            <p className='text-xl text-gray-600 mb-4'>
              Your cart is empty or has expired
            </p>
            <p className='text-gray-500 mb-6'>
              Please add items to your cart to proceed with checkout
            </p>
            <Button onClick={() => navigate('/search')} color='primary'>
              Continue Shopping
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>Checkout</h1>
          <p className='text-gray-600 mt-2'>Complete your order</p>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
          {/* Order Summary */}
          <div className='space-y-6'>
            <Card>
              <CardHeader>
                <h2 className='text-xl font-semibold'>Order Summary</h2>
              </CardHeader>
              <CardBody className='space-y-4'>
                {cart?.items?.map(item => (
                  <div
                    key={item.productId}
                    className='border-b border-gray-100 pb-4 last:border-b-0 last:pb-0'
                  >
                    <div className='flex justify-between items-start'>
                      <div className='flex-1'>
                        <h3 className='font-medium text-gray-900'>
                          {item.productName}
                        </h3>
                        <p className='text-sm text-gray-600'>
                          Qty: {item.quantity}
                        </p>

                        {/* Material Information */}
                        {item.selectedMaterial && (
                          <p className='text-sm text-gray-600'>
                            Material: {item.selectedMaterial.displayName}
                          </p>
                        )}

                        {/* Extras Information */}
                        {item.selectedExtras &&
                          item.selectedExtras.length > 0 && (
                            <p className='text-sm text-gray-600'>
                              Extras:{' '}
                              {item.selectedExtras
                                .map(
                                  (extra: SelectedExtra) => extra.displayName
                                )
                                .join(', ')}
                            </p>
                          )}

                        {/* Special Instructions */}
                        {item.specialInstructions && (
                          <p className='text-sm text-gray-600'>
                            Notes: {item.specialInstructions}
                          </p>
                        )}
                      </div>

                      <div className='text-right'>
                        <span className='font-medium text-gray-900'>
                          {formatAmount(item.totalPrice)}
                        </span>

                        {/* Show pricing breakdown for items with customizations */}
                        {(item.selectedMaterial ||
                          (item.selectedExtras &&
                            item.selectedExtras.length > 0)) && (
                          <div className='text-xs text-gray-500 mt-1 space-y-1'>
                            <div>
                              {formatAmount(item.basePrice)} base ×{' '}
                              {item.quantity}
                            </div>
                            {item.selectedMaterial &&
                              item.selectedMaterial.priceModifier !== 0 && (
                                <div>
                                  {formatAmount(
                                    item.selectedMaterial.priceModifier
                                  )}{' '}
                                  material × {item.quantity}
                                </div>
                              )}
                            {item.selectedExtras &&
                              item.selectedExtras.length > 0 && (
                                <div>
                                  +
                                  {formatAmount(
                                    item.selectedExtras.reduce(
                                      (sum, extra) => sum + extra.price,
                                      0
                                    )
                                  )}{' '}
                                  extras × {item.quantity}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div className='space-y-2'>
                  <div className='flex justify-between'>
                    <span>Subtotal</span>
                    <span>{formatAmount(getCartTotal())}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Shipping</span>
                    <span>
                      {getShippingCost() !== undefined
                        ? formatAmount(getShippingCost()!)
                        : 'Calculating...'}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Tax (included)</span>
                    <span>
                      {getTaxAmount() !== undefined
                        ? formatAmount(getTaxAmount()!)
                        : 'Calculating...'}
                    </span>
                  </div>
                  <div className='flex justify-between text-lg font-semibold'>
                    <span>Total</span>
                    <span>{formatAmount(getTotal())}</span>
                  </div>
                  <div className='text-xs text-gray-500'>
                    Tax is included in prices and shown for information only.
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Order Notes - shown for all */}
            <Card>
              <CardHeader>
                <h2 className='text-xl font-semibold'>Order Notes</h2>
              </CardHeader>
              <CardBody>
                <Textarea
                  label='Order Notes (Optional)'
                  value={formData.notes || ''}
                  onValueChange={value => handleInputChange('notes', value)}
                  placeholder='Any special instructions for your order...'
                />
              </CardBody>
            </Card>
          </div>

          {/* Checkout steps */}
          <div className='space-y-6'>
            {checkoutStep === 'auth' && !(isAuthenticated && user?.email) && (
              <CheckoutAuthChoice
                onContinueToAddress={(email, phone) => {
                  if (email) setGuestEmail(email);
                  if (phone) setGuestPhone(phone);
                  setCheckoutStep('payment');
                }}
              />
            )}
            {(checkoutStep === 'payment' ||
              (isAuthenticated && user?.email)) && (
              <Card>
                <CardHeader>
                  <h2 className='text-xl font-semibold'>Payment</h2>
                </CardHeader>
                <CardBody>
                  {!isPaymentConfigLoaded && (
                    <div className='flex items-center space-x-2 text-gray-500'>
                      <Spinner size='sm' />
                      <span>Loading payment form...</span>
                    </div>
                  )}

                  {isPaymentConfigLoaded && stripePromise && (
                    <StripeCheckout
                      amount={getTotal()}
                      paymentIntentData={paymentIntentData}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      onInitiatePayment={initiatePayment}
                      disabled={
                        !isPaymentReady ||
                        isCreatingOrder ||
                        isCreatingPayment ||
                        isEstimating
                      }
                      canInitiatePayment={isPaymentReady}
                      isInitializing={
                        !paymentIntentData?.clientSecret &&
                        (isCreatingOrder || isCreatingPayment)
                      }
                      stripePromise={stripePromise}
                    />
                  )}

                  {isPaymentConfigLoaded && !stripePromise && (
                    <div className='text-center py-8'>
                      <p className='text-red-600'>
                        Payment system is not available. Please try again later.
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
